import Template from "../app/Template";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useParams, useSearchParams } from "react-router-dom";
import Button from "../layout/Button";
import Card from "../layout/Card";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import TextInputSubmitOnEnterLeadingIcon from "../forms/TextInputSubmitOnEnterLeadingIcon";
import SectionHeader from "../layout/SectionHeader";
import { useEffect, useState } from "react";
import { getCategoryOptions } from "../../slices/category";
import { getOtherContent, getTopicMetadata, getTopicTags, searchContent } from "../../slices/content";
import Spinner from "../layout/Spinner";
import ExploreTopicsAndCollections from "../layout/ExploreTopicsAndCollections";
import Quote from "../landing/Quote";
import BecomeAMember from "../landing/BecomeAMember";
import OtherContent from "../layout/OtherContent";
import PostCard from "../layout/PostCard";
import { Media, Post } from "../../types/content";
import { consumptionFilter, withFilters } from "../../utils/searchParams";
import ConsumptionFilters from "../layout/ConsumptionFilters";


export default function TopicBySlug() {
  const dispatch = useAppDispatch();

  // Get URL params
  const { topic_slug: topicSlug } = useParams()

  // Get query params
  const [searchParams, setSearchParams] = useSearchParams();
  const querySearch = searchParams.get("query");
  const queryInterest = searchParams.get("interest");
  const queryAudience = searchParams.get("audience");
  const queryConsumption = searchParams.get("consumption");
  const queryType = searchParams.get("type") || null;
  const queryTag = searchParams.get("tag") || "";
  const queryPageNumber = searchParams.get("page") === null
    ? 1
    : parseInt(searchParams.get("page") as string);

  const { user, loading: authLoading } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (authLoading) return;
    dispatch(getTopicTags({ topicSlug, pageNumber: 1, pageSize: 8 }));
    dispatch(getTopicMetadata({ topicSlug }));
    dispatch(searchContent({
      pageNumber: queryPageNumber,
      query: querySearch,
      contentType: queryType,
      consumption: queryConsumption,
      topic: topicSlug === undefined ? null : topicSlug,
      tag: queryTag,
      audience: queryAudience,
    }));
    dispatch(getOtherContent({
      pageNumber: 1,
      contentType: "Events",
      topic: topicSlug === undefined ? null : topicSlug,
      collection: null
    }));
  }, [
    authLoading,
    querySearch,
    topicSlug,
    queryInterest,
    queryAudience,
    queryConsumption,
    queryPageNumber,
    queryType,
    queryTag
  ]);

  useEffect(() => {
    dispatch(getCategoryOptions())
  }, []);

  // Get app level state from redux store
  const {
    search: { data: searchData, loading: searchLoading, waiting: searchWaiting, pagination: searchPagination },
    events: { data: eventsData, loading: eventsLoading },
    peoples: { data: peoplesData, loading: peoplesLoading },
    tools: { data: toolsData, loading: toolsLoading },
    supports: { data: supportData, loading: supportLoading },
    tags: { data: tagsData, loading: tagsLoading },
    topic: { data: topicData, loading: topicLoading },
  } = useAppSelector((state) => state.content);
  const {
    options: { data: optionsData, loading: optionsLoading }
  } = useAppSelector((state) => state.category);

  // Component level state
  const [searchState, setSearchState] = useState("");
  const [otherContentFilter, setOtherContentFilter] = useState("Events");

  /**
   * Apply a refine-search filter.
   *
   * Every one of them clears `type`. `type` selects which *table* the search endpoint
   * queries — Post or Media — not which badge the content carries, and the
   * Listen/Watch buttons used to pin `type=Media` alongside the consumption value.
   * Everything the ingestion pipeline publishes is a Post row, so that pinning hid
   * every pipeline video and podcast from Listen and Watch while Read (`type=Post`)
   * went on looking fine. Consumption on its own searches both tables, which is what
   * /search has always done.
   */
  const applyFilters = (changes: Record<string, string | null>) => {
    setSearchParams(withFilters(searchParams, changes));
  };

  // Constant variables
  return (
    <Template navbarMode="solid" footerBgColour="off-white">

      <div className="grid grid-col-1 gap-y-20 mb-20">
        {/* Hero */}
        <div>
          <div
            className="mt-40 flex items-center justify-center w-9/10 lg:w-4/5 mx-auto bg-off-white relative"
          >
            <Card colour="yellow" className="w-full grid grid-cols-1 gap-y-4">
              <span className="bg-white px-6 py-2 rounded-full text-black w-fit shadow-md text-base font-bold">Topic</span>
              <h1 className="text-dark-grey text-3xl md:text-5xl lg:text-6xl font-bold">{topicData === null ? "Loading..." : topicData.name}</h1>
            </Card>

            {/* Bubbles */}
            <img
              src="/img/graphics/bubbles/bubble-top-orange.svg"
              alt="Bubble"
              className="absolute w-24 object-cover top-1/2 right-4 z-0"
            />
            <img
              src="/img/graphics/bubbles/bubble-right-blue.svg"
              alt="Bubble"
              className="absolute w-48 object-cover -bottom-10 right-28 z-0"
            />
          </div>

          {/* What are you interested in today? */}
          {/* <div
            className="mt-16 flex items-center justify-center w-9/10 lg:w-4/5 mx-auto bg-off-white relative"
          >
            <Card colour="dark-beige" className="w-full grid grid-cols-3 gap-y-4">

              
              <h1 className="text-dark-grey text-3xl md:text-5xl lg:text-6xl font-bold">What are you interested in today?</h1>

              
              <div className="col-span-2 h-full grid grid-cols-1 justify-center items-center gap-y-4 px-4 my-auto">

              
                <TextInputSubmitOnEnterLeadingIcon
                  id="search-library"
                  placeholder="Search our library"
                  icon={
                    <MagnifyingGlassIcon
                      aria-hidden="true"
                      className={`pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-dark-grey sm:size-4`}
                    />
                  }
                  widthClass="w-full"
                  onChange={(e) => setSearchState(e.target.value)}
                  onClick={() => {
                    if (searchState !== "") {
                      const updatedSearchParams = searchParams;
                      searchParams.forEach((value: any, key: string) => {
                        updatedSearchParams.set(key, value);
                      })
                      updatedSearchParams.set("query", searchState);
                      setSearchParams(updatedSearchParams)
                    }
                  }}
                  loading={false}
                  shadow="md"
                />

                
                <div className="flex flex-wrap gap-2 justify-center items-center px-8 md:px-10 lg:px-12">
                  {tagsLoading ? <Spinner size={8} /> : tagsData.map((obj, index) => {
                    return (
                      <Button
                        key={`filter-${index}`}
                        onClick={() => {
                          const updatedSearchParams = searchParams;
                          searchParams.forEach((value: any, key: string) => {
                            updatedSearchParams.set(key, value);
                          })
                          updatedSearchParams.set("tag", obj.name);
                          setSearchParams(updatedSearchParams)
                        }}
                        width="fit"
                        bgColour={queryTag !== obj.name ? `transparent hover:bg-dark-grey` : `dark-grey`}
                        borderColour="dark-grey"
                        textColour={queryTag !== obj.name ? `dark-grey hover:text-white` : `white`}
                      >
                        {obj.name}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </Card>


            <img
              src="/img/graphics/bubbles/bubble-top-orange.svg"
              alt="Bubble"
              className="absolute w-24 object-cover -top-4 -right-28 z-0"
            />
            <img
              src="/img/graphics/bubbles/bubble-right-blue.svg"
              alt="Bubble"
              className="absolute w-48 object-cover top-10 -right-28 z-0"
            />
          </div> */}

          <div
            className="mt-8 grid grid-cols-1 items-center justify-center mx-auto bg-off-white"
          >

            {/* Filters */}
            <div className="flex flex-row flex-wrap gap-x-4 justify-center items-center">
              <span className="font-semibold text-dark-grey text-base">Refine search</span>

              {/* Consumption filters */}
              <ConsumptionFilters
                selected={queryConsumption}
                onSelect={(consumption) => applyFilters(consumptionFilter(consumption))}
              />

            </div>

            {/* Content */}
            {searchLoading ? <div className="w-full flex justify-center mx-auto my-20"><Spinner size={8} /></div> : (
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-16 md:px-20 lg:px-32">
                {searchData.map((content: Post | Media, index) => {
                  let contentObj: any = null;
                  if (content.content_type === "Post") {
                    contentObj = (
                      <PostCard
                        key={`content-${index}`}
                        objectId={content.id}
                        objectType={content.content_type}
                        image={content.images.length === 0 ? "/img/graphics/hero/landing-hero.png" : content.images[0]}
                        bgColour="dark-beige"
                        href={content.url}
                        title={content.display_title}
                        authors={content.authors}
                        primaryTags={content.display_primary_tags}
                        synopsis={content.synopsis}
                        consumption={content.consumption}
                        user={user}
                        rating={content.rating}
                        reviews={content.reviews}
                        price={content.price}
                        locked={content.locked}
                      />
                    )
                  } else if (content.content_type === "Media") {
                    contentObj = (
                      <PostCard
                        key={`content-${index}`}
                        objectId={content.id}
                        // Not "Post": the rating and favourite calls name the table the
                        // id lives in, and a Media id looked up as a Post is a 400.
                        objectType={content.content_type}
                        image={content.images.length === 0 ? "/img/graphics/hero/landing-hero.png" : content.images[0]}
                        bgColour="dark-beige"
                        href={content.url}
                        title={content.display_title}
                        authors={content.authors}
                        primaryTags={content.display_primary_tags}
                        synopsis={content.synopsis}
                        consumption={content.consumption}
                        user={user}
                        rating={content.rating}
                        reviews={content.reviews}
                        price={content.price}
                        locked={content.locked}
                      />
                    )
                  }
                  return contentObj;

                })}
              </div>
            )}

            {/* Pagination */}
            {searchPagination.has_next && (
              <div className="mt-8 flex items-center justify-center">
                {searchWaiting ? <Spinner size={6} /> : (
                  <Button
                    onClick={() => {
                      searchParams.set("page", (queryPageNumber + 1).toString());
                      setSearchParams(searchParams)
                    }}
                    width="fit"
                    bgColour="transparent hover:bg-dark-grey"
                    borderColour="dark-grey"
                    textColour="dark-grey hover:text-white"
                  >
                    Show more
                  </Button>
                )}
              </div>
            )}

          </div>
        </div>

        {/* Quote */}
        <Quote text={topicLoading ? "" : topicData?.quote.text} subtext={topicLoading ? "" : `${topicData?.quote.author}, ${topicData?.quote.source}`} />

        {/* Other content */}
        <OtherContent topic={topicSlug} collection={undefined} />

        {/* You may also like */}
        <SectionHeader
          text="You may also like"
        />

        {/* Topics and collections */}
        <ExploreTopicsAndCollections />

        {/* Become a member */}
        <BecomeAMember />

      </div>

    </Template>
  );
}
